import { useCallback, useEffect, useMemo, useState } from 'react';

// Use deferred storage queue for persisting story flags.
import { queueJsonStorageWrite } from '../persistence/storageQueue';
import storyline1Img from '../../assets/story_opt/storyline1-1080.webp';
import { getEligibleStorylets, getStoryletById } from './registry';
import { applyEffects, checkReqs } from './effects';

export function useStoryletFlow({
  playerPos,
  setMap,
  
  keys,
  teeth,
  potions,
  torches,

  maxKeys,
  maxTeeth,
  maxPotions,
  maxTorches,

  setSanity,
  setKeys,
  setTeeth,
  setPotions,
  setTorches,

  gainTrinketById,
  gainRandomTrinket,
  addCardToDeck,
  gainRandomCard,
  startCombatEncounter,
}) {
  const [isStorylineActive, setIsStorylineActive] = useState(false);
  const [activeStorylet, setActiveStorylet] = useState(null);
  const [storySceneId, setStorySceneId] = useState(null);
  const [storyStepIndex, setStoryStepIndex] = useState(0);

  const [storyFlags, setStoryFlags] = useState(() => {
    try {
      const raw = localStorage.getItem('dd_story_flags_v1');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    queueJsonStorageWrite('dd_story_flags_v1', storyFlags);
  }, [storyFlags]);

  const [cardState, setCardState] = useState('story');
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [storyDragX, setStoryDragX] = useState(0);
  const [isStoryStepFlipping, setIsStoryStepFlipping] = useState(false);
  const [storyNextBg, setStoryNextBg] = useState(null);

  const resourceSnapshot = useMemo(
    () => ({ keys, teeth, potions, torches }),
    [keys, teeth, potions, torches]
  );

  const storyCtx = useMemo(
    () => ({
      addSanity: (d) => {
        setSanity((s) => s + (Number(d) || 0));
      },

      addResource: (key, d) => {
        const delta = Number(d) || 0;

        switch (key) {
          case 'keys':
            return setKeys((k) => Math.max(0, Math.min(maxKeys, k + delta)));
          case 'teeth':
            return setTeeth((t) => Math.max(0, Math.min(maxTeeth, t + delta)));
          case 'potions':
            return setPotions((p) => Math.max(0, Math.min(maxPotions, p + delta)));
          case 'torches':
            return setTorches((t) => Math.max(0, Math.min(maxTorches, t + delta)));
          default:
            return;
        }
      },

      setFlag: (key, value) => {
        const k = String(key || '');
        if (!k) return;
        setStoryFlags((f) => ({ ...f, [k]: value }));
      },

      gainTrinketById: (relicId) => gainTrinketById?.(relicId),
      gainRandomTrinket: (payload) => gainRandomTrinket?.(payload),
      addCardToDeck: (cardId) => addCardToDeck?.(cardId),
      gainRandomCard: (payload) => gainRandomCard?.(payload),
      startCombat: ({ encounterId, enemyId, difficulty, resumeStorylet, resumeScene } = {}) => {
        const requestedResumeStorylet = String(resumeStorylet || '').trim();
        const requestedResumeScene = String(resumeScene || '').trim();
        const resolvedResumeStorylet =
          requestedResumeStorylet === '__current__'
            ? String(activeStorylet?.id || '').trim()
            : requestedResumeStorylet || (requestedResumeScene ? String(activeStorylet?.id || '').trim() : '');

        const startedEncounterId = startCombatEncounter?.(encounterId || enemyId, difficulty, {
          resumeStorylet: resolvedResumeStorylet,
          resumeScene: requestedResumeScene,
        });

        return {
          started: Boolean(startedEncounterId),
          hasResume: Boolean(resolvedResumeStorylet),
        };
      },
    }),
    [
      addCardToDeck,
      gainRandomCard,
      gainRandomTrinket,
      gainTrinketById,
      maxKeys,
      maxTeeth,
      maxPotions,
      maxTorches,
      setSanity,
      setKeys,
      setTeeth,
      setPotions,
      setTorches,
      startCombatEncounter,
      activeStorylet,
    ]
  );

  const getActiveStep = useCallback(() => {
    if (!activeStorylet || !storySceneId) return null;
    const scene = activeStorylet.scenes?.[storySceneId];
    if (!scene || !Array.isArray(scene.steps)) return null;
    return scene.steps[storyStepIndex] || null;
  }, [activeStorylet, storySceneId, storyStepIndex]);

  const markStoryletComplete = useCallback((storyletId) => {
    const id = String(storyletId || '').trim();
    if (!id) return;
    setStoryFlags((f) => ({ ...f, [`story_done_${id}`]: true }));
  }, []);

  const exitStoryline = useCallback(({ markComplete = false } = {}) => {
    if (markComplete && activeStorylet?.id) {
      markStoryletComplete(activeStorylet.id);
    }

    setIsStorylineActive(false);
    setActiveStorylet(null);
    setStorySceneId(null);
    setStoryStepIndex(0);
    setSwipeDirection(null);
    setStoryDragX(0);
    setIsStoryStepFlipping(false);
    setStoryNextBg(null);

    setMap((prev) => {
      if (!Array.isArray(prev) || !prev.length) return prev;

      const x = Number(playerPos?.x);
      const y = Number(playerPos?.y);
      if (!Number.isInteger(x) || !Number.isInteger(y)) return prev;
      if (!Array.isArray(prev[y]) || typeof prev[y][x] === 'undefined') return prev;

      const next = prev.map((row) => row.slice());
      next[y][x] = 'floor';
      return next;
    });
  }, [activeStorylet, markStoryletComplete, playerPos, setMap]);

  const openStoryletById = useCallback((storyletId, sceneId) => {
    const id = String(storyletId || '').trim();
    if (!id) return false;

    const target = getStoryletById(id);
    if (!target) return false;

    const requestedSceneId = String(sceneId || '').trim();
    const resolvedSceneId =
      requestedSceneId && target.scenes?.[requestedSceneId]
        ? requestedSceneId
        : target.startScene || Object.keys(target.scenes || {})[0] || 'start';

    setStoryFlags((f) => {
      const { story_pending, ...rest } = f || {};
      return rest;
    });
    setActiveStorylet(target);
    setStorySceneId(resolvedSceneId);
    setStoryStepIndex(0);
    setIsStorylineActive(true);
    setCardState('story');
    setSwipeDirection(null);
    setStoryDragX(0);
    setIsStoryStepFlipping(false);
    setStoryNextBg(null);
    return true;
  }, []);

  const startStoryline = useCallback(() => {
    const resources = resourceSnapshot;

    const pendingId = storyFlags?.story_pending;
    if (pendingId) {
      if (storyFlags[`story_done_${pendingId}`]) {
        setStoryFlags((f) => {
          const { story_pending, ...rest } = f;
          return rest;
        });
      } else if (openStoryletById(pendingId)) {
        return;
      } else {
        setStoryFlags((f) => {
          const { story_pending, ...rest } = f;
          return rest;
        });
      }
    }

    const poolRaw = getEligibleStorylets(storyFlags, resources);
    const pool = (poolRaw || []).filter((s) => !storyFlags[`story_done_${s?.id}`]);

    if (!pool || pool.length === 0) return;

    const s = pool[Math.floor(Math.random() * pool.length)];

    setActiveStorylet(s);
    setStorySceneId(s.startScene);
    setStoryStepIndex(0);

    setIsStorylineActive(true);
    setCardState('story');
    setSwipeDirection(null);
    setStoryDragX(0);
  }, [openStoryletById, resourceSnapshot, storyFlags]);

  const advanceStoryTap = useCallback(() => {
    const step = getActiveStep();
    if (!step) return;

    if (step.type === 'page') {
      if (isStoryStepFlipping) return;

      const scene = activeStorylet?.scenes?.[storySceneId];
      const next = storyStepIndex + 1;

      if (scene && next < scene.steps.length) {
        const nextStep = scene.steps[next];
        const nextBg = nextStep?.image || activeStorylet?.coverImage || storyline1Img;

        setStoryNextBg(nextBg);
        setIsStoryStepFlipping(true);

        setTimeout(() => {
          setStoryStepIndex(next);
          setIsStoryStepFlipping(false);
          setStoryNextBg(null);
        }, 250);
      } else {
        exitStoryline({ markComplete: true });
      }
      return;
    }

    if (step.type === 'ending') {
      if (step.effects) applyEffects(step.effects, storyCtx);
      exitStoryline({ markComplete: true });
    }
  }, [
    activeStorylet,
    exitStoryline,
    getActiveStep,
    isStoryStepFlipping,
    storyCtx,
    storySceneId,
    storyStepIndex,
  ]);

  const handleStorySwipe = useCallback(
    (direction) => {
      const step = getActiveStep();
      if (!step || step.type !== 'choice') return;
      if (swipeDirection) return;

      const opt = direction === 'left' ? step.left : step.right;
      if (!opt) return;

      if (opt.if && !checkReqs(opt.if, { flags: storyFlags, resources: resourceSnapshot })) {
        return;
      }

      setSwipeDirection(direction);

      setTimeout(() => {
        const effectMeta = opt.effects ? applyEffects(opt.effects, storyCtx) : null;

        const currentId = activeStorylet?.id;
        if (currentId) {
          markStoryletComplete(currentId);
        }

        if (effectMeta?.combatHasResume) {
          exitStoryline({ markComplete: true });
          setSwipeDirection(null);
          return;
        }

        if (opt.nextStorylet) {
          setStoryFlags((f) => ({ ...f, story_pending: opt.nextStorylet }));
        }

        if (opt.nextScene) {
          setStorySceneId(opt.nextScene);
          setStoryStepIndex(0);
        } else {
          exitStoryline({ markComplete: true });
        }

        setSwipeDirection(null);
      }, 350);
    },
    [
      activeStorylet,
      exitStoryline,
      getActiveStep,
      markStoryletComplete,
      resourceSnapshot,
      storyCtx,
      storyFlags,
      swipeDirection,
    ]
  );

  return {
    isStorylineActive,
    activeStorylet,
    storySceneId,
    storyStepIndex,
    storyFlags,
    cardState,
    swipeDirection,
    storyDragX,
    setStoryDragX,
    isStoryStepFlipping,
    storyNextBg,
    startStoryline,
    openStoryletById,
    getActiveStep,
    exitStoryline,
    advanceStoryTap,
    handleStorySwipe,
  };
}